import { Controller } from "@hotwired/stimulus"

// Client-side validation for portal service catalog form submission.
// Validates required fields before allowing form submit. Shows inline
// errors per field and clears them on input.
//
// data-controller="support--catalog-form"
export default class extends Controller {
  static targets = ["form", "submitBtn"]

  // CONEK-5794: evaluate conditions on first paint, not only on the first user
  // interaction. conditionsChanged is wired via data-action (change/input), so
  // without this every conditional field rendered VISIBLE until something on the
  // page changed — the exact failure the feature exists to prevent. The server
  // never emits an initial `hidden` attribute, so the DOM starts all-visible and
  // this is what corrects it. Idempotent, so running it here is safe.
  connect() {
    this.conditionsChanged()
  }
  static values = {
    requiredMessage: { type: String, default: "This field is required" },
    submittingMessage: { type: String, default: "Submitting..." },
    submitLabel: { type: String, default: "Submit" },
  }

  submit(event) {
    if (!this._validate()) {
      event.preventDefault()
    } else {
      this._setLoading(true)
    }
  }

  clearError(event) {
    const field = event.target
    const errorEl = this._errorElementFor(field)
    if (errorEl) {
      errorEl.textContent = ""
      errorEl.classList.add("hidden")
    }
    field.classList.remove("border-red-500")
  }

  _validate() {
    const form = this.hasFormTarget ? this.formTarget : this.element
    const requiredFields = form.querySelectorAll("[required]")
    let valid = true

    requiredFields.forEach((field) => {
      // CONEK-5794: a required field inside a hidden conditional block must not
      // block submission — the user cannot see it, let alone fill it.
      if (this._isHidden(field)) return

      if (!this._isFilled(field)) {
        valid = false
        field.classList.add("border-red-500")
        const errorEl = this._errorElementFor(field)
        if (errorEl) {
          errorEl.textContent = this.requiredMessageValue
          errorEl.classList.remove("hidden")
        }
      }
    })

    return valid
  }

  // CONEK-5792: reading field.value for every input type let a required
  // checkbox pass client validation while unchecked — Ui::CheckboxComponent
  // renders a static value="true", so .value was truthy regardless of state.
  // The same applied to file inputs, whose .value is "" until a file is picked
  // but whose hidden companion input carries a value.
  // CONEK-5794: conditional visibility. Each conditional wrapper declares the
  // field it watches and the value that reveals it; we toggle on any change so a
  // dependent field appears as soon as its controlling answer matches.
  conditionsChanged() {
    const form = this.hasFormTarget ? this.formTarget : this.element

    form.querySelectorAll("[data-visible-if-field]").forEach((wrapper) => {
      wrapper.hidden = !this._isVisible(form, wrapper, new Set())
    })
  }

  // CONEK-5799: mirrors SupportTickets::CreateFromCatalog#field_visible?
  // exactly, including the operator precedence. If these two ever disagree,
  // a field the user cannot see becomes required (or a hidden answer gets
  // submitted) — so keep them changing together.
  _isVisible(form, wrapper, seen) {
    const watchedKey = wrapper.dataset.visibleIfField
    if (watchedKey === undefined) return true

    const current = this._answerFor(form, watchedKey)
    const given = Array.isArray(current)
      ? current.map(String)
      : [String(current ?? "")]

    let matches
    if (wrapper.dataset.visibleIfAny !== undefined) {
      const wanted = this._parseList(wrapper.dataset.visibleIfAny)
      matches = given.some((g) => wanted.includes(g))
    } else if (wrapper.dataset.visibleIfNone !== undefined) {
      const excluded = this._parseList(wrapper.dataset.visibleIfNone)
      // The complement of "nothing chosen" is everything, so an unanswered
      // controlling field leaves the dependent one visible.
      matches = !given.some((g) => excluded.includes(g))
    } else {
      matches = given.includes(wrapper.dataset.visibleIfEquals ?? "")
    }
    if (!matches) return false

    // CONEK-9335: a chain. If the controlling question is itself hidden, so is
    // this one — its answer is still in the DOM but nobody can see or change
    // it. `seen` breaks a cycle in a hand-edited schema, as on the server.
    if (seen.has(watchedKey)) return true
    const parent = form.querySelector(
      `[data-field-key="${CSS.escape(watchedKey)}"]`,
    )
    if (!parent) return true

    return this._isVisible(form, parent, new Set([...seen, watchedKey]))
  }

  // The list operators travel as a JSON array in the data attribute; a malformed
  // value yields an empty list rather than throwing mid-render.
  _parseList(raw) {
    try {
      const parsed = JSON.parse(raw ?? "[]")
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }

  // Reads the current answer for a schema field key, handling the multiselect
  // `form_responses[key][]` naming as well as scalar inputs.
  _answerFor(form, key) {
    const multi = form.querySelectorAll(`[name="form_responses[${key}][]"]`)
    if (multi.length > 0) {
      // The ""-valued hidden sentinel Ui::MultiSelectComponent emits is not an
      // answer; including it would let `visible_if: equals ""` match an empty
      // multiselect.
      return Array.from(multi)
        .filter((el) => (el.type === "checkbox" ? el.checked : true))
        .map((el) => el.value)
        .filter((v) => v !== "")
    }

    const el = form.querySelector(`[name="form_responses[${key}]"]`)
    if (!el) return null
    if (el.type === "checkbox") return el.checked ? el.value : ""

    return el.value
  }

  // True when the field sits inside a conditional wrapper that is currently
  // hidden. Checked against the wrapper rather than computed styles so it works
  // headlessly in tests too.
  _isHidden(field) {
    const wrapper = field.closest("[data-visible-if-field]")
    return Boolean(wrapper?.hidden)
  }

  _isFilled(field) {
    if (field.type === "checkbox" || field.type === "radio")
      return field.checked
    if (field.type === "file") return field.files && field.files.length > 0

    return Boolean(field.value?.trim())
  }

  _errorElementFor(field) {
    // CatalogFormFieldComponent renders the error placeholder with
    // data-error-for=field.name (e.g. "form_responses[key]"). Ui::* components
    // generate their own ids so we look up by name first, falling back to id
    // for any field that doesn't follow the convention.
    const name = field.name
    const id = field.id
    return (
      (name && this.element.querySelector(`[data-error-for="${name}"]`)) ||
      (id && this.element.querySelector(`[data-error-for="${id}"]`)) ||
      null
    )
  }

  _setLoading(loading) {
    if (!this.hasSubmitBtnTarget) return

    if (loading) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.dataset.originalText =
        this.submitBtnTarget.textContent
      this.submitBtnTarget.textContent = this.submittingMessageValue
    } else {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent =
        this.submitBtnTarget.dataset.originalText || this.submitLabelValue
    }
  }
}
